import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './AuthPages.css';

const MEMBERSHIP_CHECKOUT_KEY = 'guhso-membership-checkout';

const PLAN_OPTIONS = [
  {
    code: 'monthly',
    name: 'Monthly Membership',
    price: '$3.99',
    cadence: '/month',
    description: 'Flexible access for listeners who want to join month to month.',
  },
  {
    code: 'annual',
    name: 'Yearly Membership',
    price: '$38.50',
    cadence: '/year',
    description: 'Best value for members who want uninterrupted access all year.',
  },
];

const RegistrationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const requestedPlan = new URLSearchParams(location.search).get('plan');
  const [selectedPlan, setSelectedPlan] = useState(requestedPlan === 'monthly' ? 'monthly' : 'annual');
  const [formState, setFormState] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    interests: '',
    promo: true,
    sms: false,
    isAdult: false,
  });
  const [errorMessage, setErrorMessage] = useState('');

  const selectedPlanMeta = useMemo(
    () => PLAN_OPTIONS.find((plan) => plan.code === selectedPlan) || PLAN_OPTIONS[1],
    [selectedPlan]
  );

  const handleChange = (event) => {
    const { name, type, checked, value } = event.target;
    setFormState((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setErrorMessage('');

    if (!formState.firstName.trim() || !formState.lastName.trim()) {
      setErrorMessage('Please enter your first and last name.');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email.trim())) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    if (!formState.isAdult) {
      setErrorMessage('You must confirm that you are 18 or older to subscribe.');
      return;
    }

    const payload = {
      ...formState,
      firstName: formState.firstName.trim(),
      lastName: formState.lastName.trim(),
      email: formState.email.trim(),
      phone: formState.phone.trim(),
      interests: formState.interests.trim(),
      planCode: selectedPlan,
      redirect: new URLSearchParams(location.search).get('redirect') || '/profile',
    };

    window.sessionStorage.setItem(MEMBERSHIP_CHECKOUT_KEY, JSON.stringify(payload));
    navigate('/membership/checkout');
  };

  return (
    <div className="auth-page">
      <div className="auth-card auth-card--wide">
        <div className="auth-header">
          <span className="badge">Join Guhso Membership</span>
          <h1>Choose your plan</h1>
          <p>Select a membership, enter your details, and continue to secure checkout.</p>
        </div>

        <div className="auth-plan-grid">
          {PLAN_OPTIONS.map((plan) => {
            const isSelected = selectedPlan === plan.code;
            return (
              <button
                key={plan.code}
                type="button"
                className={`auth-plan-choice ${isSelected ? 'selected' : ''}`}
                onClick={() => setSelectedPlan(plan.code)}
              >
                <div className="auth-plan-choice__header">
                  <h2>{plan.name}</h2>
                  <span className="auth-plan-choice__indicator" aria-hidden="true">
                    <i className="fas fa-crown"></i>
                  </span>
                </div>
                <p className="auth-plan-choice__price">
                  <strong>{plan.price}</strong>
                  <span>{plan.cadence}</span>
                </p>
                <p className="auth-plan-choice__description">{plan.description}</p>
              </button>
            );
          })}
        </div>

        {errorMessage && (
          <div className="auth-alert auth-alert--danger" role="alert" aria-live="assertive">
            {errorMessage}
          </div>
        )}

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="firstName">First Name</label>
              <input id="firstName" name="firstName" type="text" value={formState.firstName} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label htmlFor="lastName">Last Name</label>
              <input id="lastName" name="lastName" type="text" value={formState.lastName} onChange={handleChange} required />
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input id="email" name="email" type="email" value={formState.email} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <input id="phone" name="phone" type="tel" value={formState.phone} onChange={handleChange} />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="interests">Content Interests</label>
            <textarea
              id="interests"
              name="interests"
              rows="3"
              value={formState.interests}
              onChange={handleChange}
              placeholder="Tell us what kind of content you want more of."
            />
          </div>

          <div className="auth-highlight">
            <strong>{selectedPlanMeta.name}</strong> selected. You’ll review and pay {selectedPlanMeta.price}
            {selectedPlanMeta.cadence} at the next step.
          </div>

          <div className="preference-list">
            <div className="checkbox-group">
              <input id="promo" name="promo" type="checkbox" checked={formState.promo} onChange={handleChange} />
              <label htmlFor="promo">Send me Guhso email updates and member announcements.</label>
            </div>
            <div className="checkbox-group">
              <input id="sms" name="sms" type="checkbox" checked={formState.sms} onChange={handleChange} />
              <label htmlFor="sms">Send me occasional SMS updates for launches and live events.</label>
            </div>
            <div className="checkbox-group">
              <input id="isAdult" name="isAdult" type="checkbox" checked={formState.isAdult} onChange={handleChange} required />
              <label htmlFor="isAdult">I confirm that I am 18 or older.</label>
            </div>
          </div>

          <button className="primary-button" type="submit">
            Continue to Checkout
          </button>
        </form>

        <div className="auth-footer">
          <span>
            Already a member? <Link to={`/signin${location.search || ''}`}>Sign in here</Link>.
          </span>
        </div>
      </div>
    </div>
  );
};

export default RegistrationPage;
